/**
 * A person's requests about the messages Connect's clients send them —
 * opt-outs, stops, exceptions, erasure (`/individual-requests`,
 * `docs/38_INDIVIDUAL_REQUESTS.md`).
 *
 * Every decision is the server's: which actions a request offers (`actions`),
 * whether the caller may take them, whether its scope is valid. The page only
 * says what each one means.
 */

import { api, type Tone } from '@connect/web-shared';

export type Status =
  | 'pending_verification'
  | 'pending_review'
  | 'processing'
  | 'active'
  | 'completed'
  | 'failed'
  | 'rejected'
  | 'withdrawn';

export type Source = 'staff' | 'public' | 'whatsapp' | 'rcs' | 'email_link' | 'email_reply';
export type Action = 'approve' | 'reject' | 'withdraw' | 'verify' | 'retry';
export type Channel = 'sms' | 'whatsapp' | 'rcs' | 'email' | 'webchat' | 'voice';

export interface RequestRow {
  public_id: string;
  code: string;
  status: Status;
  source: Source;
  type_key: string;
  type_label: string;
  erasure: boolean;
  people: string | null;
  business_hint: string | null;
  last_error: string | null;
  created_at: string;
  applied_at: string | null;
  verified: boolean;
  /** The person is owed an answer by then. */
  respond_by: string | null;
  /** When their messages stopped — for a proven erasure, before review. */
  held_at: string | null;
}

export interface Named {
  public_id: string;
  name: string;
  kind?: string;
}

export interface ScopeView {
  categories: string[];
  channels: Channel[];
  clients: Named[];
  except_clients: Named[];
  allow_clients: Named[];
  classifications: Named[];
  except_classifications: Named[];
  allow_classifications: Named[];
}

export interface ScopeIn {
  categories: string[];
  channels: Channel[];
  clients: string[];
  classifications: string[];
  except_clients: string[];
  except_classifications: string[];
  allow_clients: string[];
  allow_classifications: string[];
}

export type Effect = 'in_scope' | 'excluded' | 'allowed' | 'outside';

/**
 * The business decides about its own customers: an erasure, and any request
 * that is not a standard stop, wait for its answer (owner, 2026-10-23).
 */
export type ContactAction = 'client_decides';

export type DecisionKind = 'erasure' | 'request' | 'stop';
export type DecisionStatus = 'pending' | 'erase' | 'keep' | 'stop' | 'allow' | 'applied' | 'unanswered';

export interface ClientDecision {
  client: Named;
  kind: DecisionKind;
  status: DecisionStatus;
  due_at: string;
  reminders: number;
  /** Why: the business's lawful reason to keep the details, or to go on messaging. */
  reason: string | null;
  decided_at: string | null;
  decided_by: string | null;
  lapsed_at: string | null;
}

export const DECISION: Record<DecisionStatus, { label: string; tone: Tone }> = {
  pending: { label: 'Waiting for its answer', tone: 'warning' },
  erase: { label: 'Deleted, as it asked', tone: 'success' },
  keep: { label: 'Kept, with its reason', tone: 'info' },
  stop: { label: 'Stopped, as it asked', tone: 'success' },
  allow: { label: 'Messaging on, with its reason', tone: 'info' },
  applied: { label: 'In force — the person’s own stop', tone: 'neutral' },
  unanswered: { label: 'No answer by the deadline', tone: 'danger' },
};

export interface RequestDetail {
  public_id: string;
  code: string;
  status: Status;
  source: Source;
  source_client: Named | null;
  type: {
    key: string;
    label: string;
    effect: 'block' | 'allow';
    erasure: boolean;
    requires_verification: boolean;
    notify_clients: boolean;
    contact_action: ContactAction;
  };
  /** Each business's answer to an erasure (`client_decides`). */
  decisions: ClientDecision[];
  identifiers: { kind: 'mobile' | 'email'; masked: string }[];
  scope: ScopeView;
  business_hint: string | null;
  stated_reason: string | null;
  share_reason: boolean;
  staff_note: string | null;
  verified_via: string | null;
  verified_at: string | null;
  verification_note: string | null;
  decided_at: string | null;
  decision_note: string | null;
  applied_at: string | null;
  held_at: string | null;
  respond_by: string | null;
  completed_at: string | null;
  last_error: string | null;
  created_at: string;
  /** Who keeps the person — `null` once the request no longer holds their details. */
  holders: { public_id: string; name: string; live: boolean; effect: Effect }[] | null;
  rules: {
    effect: 'block' | 'allow';
    categories: string[];
    channels: string[];
    level: 'all' | 'classifications' | 'clients';
    active: boolean;
    ended_reason: string | null;
    effective_at: string;
  }[];
  tasks: {
    kind: string;
    client: string | null;
    status: 'pending' | 'done' | 'failed' | 'skipped';
    attempts: number;
    last_error: string | null;
    done_at: string | null;
  }[];
  events: { kind: string; actor: string; detail: Record<string, unknown>; created_at: string }[];
  actions: string[];
}

export interface RequestType {
  public_id: string;
  key: string;
  label: string;
  description: string;
  effect: 'block' | 'allow';
  categories: string[];
  erasure: boolean;
  auto_apply: boolean;
  requires_verification: boolean;
  notify_clients: boolean;
  spares_requested: boolean;
  contact_action: ContactAction;
  client_response_days: number;
  history_retention_days: number;
  blocks_readd: boolean;
  needs_clients: boolean;
  active: boolean;
}

export interface Category {
  public_id: string;
  key: string;
  label: string;
  description: string;
  restrictable: boolean;
}

export interface Mapping {
  id: number;
  channel: Channel | '*';
  word: string;
  category: string;
}

export interface Settings {
  channels: Channel[];
  categories: Category[];
  mappings: Mapping[];
  types: RequestType[];
}

export interface Classification {
  public_id: string;
  kind: 'business_type' | 'group';
  name: string;
  description: string;
  clients: number;
}

export interface BulkResult {
  done: number;
  codes: string[];
  refused: { code: string; reason: string }[];
}

export interface CreateBody {
  type: string;
  identifiers: string[];
  scope?: ScopeIn;
  stated_reason?: string;
  share_reason?: boolean;
  staff_note?: string;
  verification_note?: string;
  apply_now?: boolean;
  confirm_override?: boolean;
}

export const BASE = '/individual-requests';

export const requestKeys = {
  all: ['individual-requests'] as const,
  list: ['individual-requests', 'list'] as const,
  counts: ['individual-requests', 'counts'] as const,
  settings: ['individual-requests', 'settings'] as const,
  classifications: ['individual-requests', 'classifications'] as const,
  members: (id: string) => ['individual-requests', 'classifications', id] as const,
  request: (id: string) => ['individual-requests', 'request', id] as const,
};

export const requestsApi = {
  counts: () => api.get<Record<string, number>>(`${BASE}/counts`),
  request: (id: string) => api.get<RequestDetail>(`${BASE}/${id}`),
  create: (body: CreateBody, key: string) =>
    api.post<RequestDetail>(BASE, body, { idempotencyKey: key }),
  update: (id: string, body: Partial<{ scope: ScopeIn; stated_reason: string; share_reason: boolean; staff_note: string }>) =>
    api.patch<RequestDetail>(`${BASE}/${id}`, body),
  verify: (id: string, note: string) => api.post<RequestDetail>(`${BASE}/${id}/verify`, { note }),
  approve: (id: string, confirmOverride = false) =>
    api.post<RequestDetail>(`${BASE}/${id}/approve`, { confirm_override: confirmOverride }),
  reject: (id: string, note: string) => api.post<RequestDetail>(`${BASE}/${id}/reject`, { note }),
  withdraw: (id: string, note: string) => api.post<RequestDetail>(`${BASE}/${id}/withdraw`, { note }),
  retry: (id: string) => api.post<RequestDetail>(`${BASE}/${id}/retry`, {}),
  bulk: (
    body: { action: Action; public_ids?: string[]; matching?: boolean; expected?: number; note?: string },
    params: Record<string, string | number | boolean | undefined>,
    key: string,
  ) => api.post<BulkResult>(`${BASE}/bulk`, body, { params, idempotencyKey: key }),
  settings: () => api.get<Settings>(`${BASE}/settings`),
  updateType: (id: string, body: Partial<RequestType>) => api.patch<Settings>(`${BASE}/settings/types/${id}`, body),
  createCategory: (body: { key: string; label: string; description: string; restrictable: boolean }) =>
    api.post<Settings>(`${BASE}/settings/categories`, body),
  updateCategory: (id: string, body: { label: string; description: string; restrictable: boolean }) =>
    api.put<Settings>(`${BASE}/settings/categories/${id}`, body),
  setMapping: (body: { channel: string; word: string; category: string }) =>
    api.put<Settings>(`${BASE}/settings/mappings`, body),
  deleteMapping: (id: number) => api.delete<Settings>(`${BASE}/settings/mappings/${id}`),
  classifications: () => api.get<Classification[]>(`${BASE}/classifications`),
  saveClassification: (id: string | null, body: { kind: string; name: string; description: string }) =>
    id
      ? api.put<Named>(`${BASE}/classifications/${id}`, body)
      : api.post<Named>(`${BASE}/classifications`, body),
  deleteClassification: (id: string) => api.delete<void>(`${BASE}/classifications/${id}`),
  members: (id: string) => api.get<Named[]>(`${BASE}/classifications/${id}/clients`),
  setMembers: (id: string, clients: string[]) => api.put<Named[]>(`${BASE}/classifications/${id}/clients`, { clients }),
};

export const STATUS: Record<Status, { label: string; tone: Tone }> = {
  pending_verification: { label: 'To verify', tone: 'warning' },
  pending_review: { label: 'To route', tone: 'warning' },
  processing: { label: 'In progress', tone: 'info' },
  active: { label: 'In force', tone: 'success' },
  completed: { label: 'Completed', tone: 'success' },
  failed: { label: 'Needs action', tone: 'danger' },
  rejected: { label: 'Rejected', tone: 'neutral' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};

export const SOURCE: Record<Source, string> = {
  staff: 'Recorded by staff',
  public: 'Website, with a code',
  whatsapp: 'STOP on WhatsApp',
  rcs: 'STOP on RCS',
  email_link: 'Unsubscribe link',
  email_reply: 'Email reply',
};

export const CHANNEL_LABEL: Record<Channel | '*', string> = {
  '*': 'Any channel',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  rcs: 'RCS',
  email: 'Email',
  webchat: 'Live chat',
  voice: 'Calls',
};

export const EFFECT: Record<Effect, { label: string; tone: Tone }> = {
  in_scope: { label: 'Covered', tone: 'danger' },
  excluded: { label: 'Left out', tone: 'neutral' },
  allowed: { label: 'Let through', tone: 'success' },
  outside: { label: 'Not covered', tone: 'neutral' },
};

export const TASK: Record<string, string> = {
  notify_client: 'Tell the business',
  erase_contacts: 'Erase its contacts of them',
  erase_customer: 'Erase Connect’s own record',
  forget_memory: 'Forget the assistant’s memory',
  client_decision: 'Wait for the business’s answer, reminding it',
  minimise_history: 'Strip them from its sending records after a year',
  tell_person: 'Tell the person it is done',
};

export const EVENT: Record<string, string> = {
  received: 'Received',
  verified: 'Verified',
  changed: 'Changed',
  applied: 'Applied',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  retried: 'Tried again',
  task_done: 'Done',
  task_failed: 'Gave up',
  failed: 'Needs a retry',
  completed: 'Completed',
  in_force: 'In force',
  messages_stopped: 'Messages stopped',
  client_decided: 'A business answered',
  client_unanswered: 'A business did not answer in time — chase it',
};

export const EMPTY_SCOPE: ScopeIn = {
  categories: [],
  channels: [],
  clients: [],
  classifications: [],
  except_clients: [],
  except_classifications: [],
  allow_clients: [],
  allow_classifications: [],
};
